import 'i18next'
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpBackend from 'i18next-http-backend'
import { merge } from 'lodash'
import { initReactI18next } from 'react-i18next'

import * as localEn from '../locales/en/translation.json'

import { isFilename } from '@/utils/isFilename'
import { logger } from '@/utils/logging'

const supportedLanguages = ['en']

type SupportedLanguage = (typeof supportedLanguages)[number]

type Translations = typeof localEn

type StripEndings<K> = K extends `${infer R}_one`
  ? R
  : K extends `${infer R}_other`
    ? R
    : K extends `${infer R}_zero`
      ? R
      : K

type PathKeys<T, P extends string = ''> = {
  [K in keyof T]: K extends string | number
    ? T[K] extends object
      ? StripEndings<`${P}${K}`> | PathKeys<T[K], `${P}${K}.`>
      : StripEndings<`${P}${K}`>
    : never
}[keyof T]

export type TranslationsKeys = PathKeys<Translations>

export const localTranslations: Record<SupportedLanguage, Translations | any> =
  {
    en: localEn,
  }

export const finalTranslations: Record<SupportedLanguage, Translations | any> =
  Object.assign({}, localTranslations)

export const getPath = async (
  lng: string[],
  supportedLanguages: readonly string[],
  loadPath?: string,
  override?: string
): Promise<string> => {
  let languageToUse = 'en'
  const usersLanguage = override ?? lng?.[0] ?? languageToUse

  if (supportedLanguages.includes(usersLanguage)) {
    languageToUse = usersLanguage
  }
  // Check if the base language is supported when region is not supported (e.g., en for en-US)
  else if (usersLanguage.includes('-')) {
    const [baseLng] = usersLanguage.split('-')
    if (supportedLanguages.includes(baseLng)) {
      languageToUse = baseLng
    }
  }

  //   const pathPrefix = window.flatfile.configService.get(
  //     'VITE_SPACES_UI_PATH_PREFIX'
  //   )
  const pathPrefix = ''
  const localPath = `${pathPrefix}/locales/${languageToUse}/translation.json`

  return localPath
}

let determinedLoadPath: string

const getI18n = (loadPath?: string, override?: string) => {
  const loggedMissingKeys = new Set<string>()

  i18n
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(HttpBackend)
    .init({
      preload: Array.from(
        new Set([
          override ??
            (supportedLanguages.includes(navigator.language)
              ? navigator.language
              : 'en'),
          'en',
        ])
      ),
      fallbackLng: 'en',
      debug: false,
      backend: {
        crossDomain: true,
        allowMultiLoading: false,
        loadPath: async (lng: string[]) => {
          const path = await getPath(
            lng,
            supportedLanguages,
            loadPath,
            override
          )
          determinedLoadPath = path
          return path
        },
      },
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: true,
      },
      saveMissing: true, //required for missing key handler
      missingKeyHandler: (
        lng: readonly string[],
        ns: string,
        key: string,
        fallbackValue: any
      ): void => {
        //check that key is not a regular string or filename
        if (
          !key.includes('.') ||
          key.match(/[\s\n\t]/) ||
          key.includes('...') ||
          isFilename(key) ||
          key.endsWith('.')
        ) {
          return
        }
        const regex = /\/locales\/(?!en\/)[^/]+\/translation\.json/
        // we expect missing keys for non english languages as there is a delay in getting things translated. This falls back to english so is not an actual missing key
        if (!loggedMissingKeys.has(key) && !regex.test(determinedLoadPath)) {
          logger.error(`Missing key: ${key}, loadPath: ${determinedLoadPath}`)
          loggedMissingKeys.add(key)
        }
      },
    })
  /* c8 ignore start */
  i18n.on('loaded', (loaded) => {
    Object.keys(loaded).forEach((lng) => {
      const loadedTranslations = i18n.getResourceBundle(
        override ?? lng,
        'translation'
      )
      let mergedTranslations = {}
      if (override && override !== 'en') {
        const englishTranslations = localTranslations.en
        if (determinedLoadPath !== `/locales/${override}/translation.json`) {
          mergedTranslations = merge(
            {},
            englishTranslations,
            localTranslations[override],
            loadedTranslations
          )
        } else {
          mergedTranslations = merge(
            {},
            englishTranslations,
            localTranslations[override]
          )
        }
      } else {
        mergedTranslations = merge(
          {},
          localTranslations[lng as SupportedLanguage],
          loadedTranslations
        )
      }
      Object.assign(finalTranslations, mergedTranslations)
      i18n.addResourceBundle(lng, 'translation', mergedTranslations)
    })
    resolveGlobalI18nLoaded()
  })
  /* c8 ignore end */
  return i18n
}

let resolveGlobalI18nLoaded: () => void

export const globalI18nLoaded = new Promise<void>(
  (resolve) => (resolveGlobalI18nLoaded = resolve)
)

export default getI18n
